import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

const identityBenefits = [
  { type: 'work_id', icon: '/images/fuli_gongzuozheng.png', label: '工作证' },
  { type: 'work_uniform', icon: '/images/fuli_gongfu.png', label: '工作服' },
  { type: 'door_plate', icon: '/images/fuli_menpau.png', label: '党员之家门牌' },
  { type: 'member_cert', icon: '/images/fuli_dnagyuan.png', label: '党员证' },
];

const premiumBenefits = [
  { type: 'official_car', icon: '/images/fuli_gongwu.png', label: '配置公务车辆' },
  { type: 'housing', icon: '/images/fuli_anzhifnag.png', label: '党员安置房分配' },
  { type: 'family_fund', icon: '/images/fuli_snabaiwna.png', label: '党员家庭补助资金300万支票' },
  { type: 'huawei_phone', icon: '/images/fuli_shouji.png', label: '党员专用华为手机' },
];

const developmentBenefits = [
  { type: 'office_subsidy', icon: '/images/fuli_changdi.png', label: '办公场地补助' },
  { type: 'transport_subsidy', icon: '/images/fuli_jaiotong.png', label: '交通补助' },
  { type: 'phone_subsidy', icon: '/images/fuli_huafei.png', label: '话费补助' },
  { type: 'team_building', icon: '/images/fuli_tuanjian.png', label: '团建补助' },
];

export default function WelfarePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<{ name?: string; member_type?: string; member_id?: string } | null>(null);
  const [claimed, setClaimed] = useState<Record<string, string>>({});
  const [claiming, setClaiming] = useState<string | null>(null);

  useEffect(() => {
    api.get('/me').then(({ data }) => setUser(data)).catch(() => navigate('/login'));
    api.get('/welfare-claims/status').then(({ data }) => {
      setClaimed(data?.claimed ?? {});
    }).catch(() => { });
  }, [navigate]);

  async function handleClaim(type: string) {
    if (claimed[type]) { toast.error('您已领取过该福利'); return; }
    setClaiming(type);
    try {
      await api.post('/welfare-claims', { type });
      toast.success('领取成功，请联系专员核实');
      setClaimed(prev => ({ ...prev, [type]: 'approved' }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '领取失败');
    } finally {
      setClaiming(null);
    }
  }

  const memberTypeLabel = user?.member_type === 'formal' ? '正式党员' : user?.member_type === 'intern' ? '实习党员' : '预备党员';

  const isFormal = user?.member_type === 'formal';

  function renderBenefitGrid(items: typeof identityBenefits) {
    return (
      <div className="grid grid-cols-2 gap-[5px]">
        {items.map((item, index) => (
          <div key={item.type} className={`px-1 py-2 ml-3 mr-1 flex items-center gap-3 ${index < 2 ? 'border-b border-[#000]' : ''}`}>
            <img src={item.icon} alt="" className="w-10 h-10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-gray-800 leading-tight">{item.label}</p>
              <button
                onClick={() => !isFormal ? toast.error('正式党员可领取福利') : handleClaim(item.type)}
                disabled={!!claimed[item.type] || claiming === item.type}
                className={`mt-1 text-[9px] font-bold px-2 py-0.5 rounded ${claimed[item.type]
                  ? 'bg-gray-300 text-gray-500'
                  : !isFormal
                    ? 'bg-gray-300 text-gray-500'
                    : 'bg-[#fff002] text-[#e6212a]'
                  }`}
              >
                {claimed[item.type] ? '已领取' : !isFormal ? '未解锁' : '>> 领取 <<'}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-3" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Header */}
      <div className="relative">
        <img src="/images/fuli_shang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* User info bar */}
      <div className="px-4 -mt-2 relative z-10">
        <div className="bg-[#910201] border border-[#cf7b62] rounded-lg px-4 py-2 flex items-center justify-between text-white text-[11px]">
          <span>党员姓名：{user?.name ?? '—'}</span>
          <span className="border-x  border-[#cf7b62] px-2 mx-2">当前职务：{memberTypeLabel}</span>
          <span>党员编号：{user?.member_id ?? '202406080001'}</span>
        </div>
      </div>

      {/* Identity benefits */}
      <div className="px-4 mt-4">
        <div className="rounded-xl overflow-hidden bg-[url(/images/fuli_kuang.png)] bg-no-repeat bg-top bg-[size:100%_100%]">
          <img src="/images/fuli_shenfen.png" alt="党员身份权益" className="w-[150px]" />
          {renderBenefitGrid(identityBenefits)}
        </div>
      </div>

      {/* Premium benefits */}
      <div className="px-4 mt-4">
        <div className="rounded-xl overflow-hidden bg-[url(/images/fuli_kuang.png)] bg-no-repeat bg-top bg-[size:100%_100%]">
          <img src="/images/fuli_gaoji.png" alt="党员高级权益" className="w-[150px]" />
          {renderBenefitGrid(premiumBenefits)}
        </div>
      </div>

      {/* Development benefits */}
      <div className="px-4 mt-4">
        <div className="rounded-xl overflow-hidden bg-[url(/images/fuli_kuang.png)] bg-no-repeat bg-top bg-[size:100%_100%]">
          <img src="/images/fuli_buzhu.png" alt="党员发展补助" className="w-[150px]" />
          {renderBenefitGrid(developmentBenefits)}
        </div>
      </div>

      {/* Introduction section */}
      <div className="mt-4 mx-4">
        <img src="/images/fuli_jieshao.png" alt="" className="w-full mb-3" />
      </div>
    </div>
  );
}
